import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Store } from '@ngrx/store';
import { Observable, map, combineLatest, BehaviorSubject } from 'rxjs';
import { CoinTransaction } from '../../models/CoinTransaction';
import { getMyCoinTransactions } from '../../state/coin-transaction/coin-transaction.actions';
import { selectCoinTransactions } from '../../state/coin-transaction/coin-transaction.selector';
import { MatTableModule } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { Router } from '@angular/router';
import { PageHeaderComponent } from '../page-header/page-header.component';
import { A11yModule } from "@angular/cdk/a11y";

@Component({
  selector: 'app-coin-transactions',
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatSelectModule,
    MatFormFieldModule,
    MatCheckboxModule,
    PageHeaderComponent,
    A11yModule
],
  templateUrl: './coin-transactions.component.html',
  styleUrl: './coin-transactions.component.css'
})
export class CoinTransactionsComponent implements OnInit {
  transactions$: Observable<CoinTransaction[]>;
  filteredTransactions$: Observable<CoinTransaction[]>;
  availableYears$: Observable<number[]>;
  selectedYear$ = new BehaviorSubject<number | null>(null);
  hideGeneratedCoins$ = new BehaviorSubject<boolean>(false);

  constructor(private store: Store, private router: Router) {
    this.transactions$ = this.store.select(selectCoinTransactions).pipe(
      map(transactions => {
        const txArray = transactions || [];
        return [...txArray].sort((a, b) => {
          const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
          const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
          return dateB - dateA; // descending order
        });
      })
    );

    this.availableYears$ = this.transactions$.pipe(
      map(transactions => {
        const years = transactions
          .map(tx => tx.createdDate ? new Date(tx.createdDate).getFullYear() : null)
          .filter((year): year is number => year !== null);
        return [...new Set(years)].sort((a, b) => b - a); // unique years, descending
      })
    );

    this.filteredTransactions$ = combineLatest([
      this.transactions$,
      this.selectedYear$,
      this.hideGeneratedCoins$
    ]).pipe(
      map(([transactions, selectedYear, hideGenerated]) => {
        let filtered = transactions;

        // Filter by year
        if (selectedYear !== null) {
          filtered = filtered.filter(tx => {
            if (!tx.createdDate) return false;
            return new Date(tx.createdDate).getFullYear() === selectedYear;
          });
        }

        // Filter out generated coins
        if (hideGenerated) {
          filtered = filtered.filter(tx => {
            return !tx.reason?.reasonText?.includes('Generiert');
          });
        }

        return filtered;
      })
    );
  }

  ngOnInit(): void {
    this.store.dispatch(getMyCoinTransactions());
  }

  onYearChange(year: number | null): void {
    this.selectedYear$.next(year);
  }

  onHideGeneratedChange(hide: boolean): void {
    this.hideGeneratedCoins$.next(hide);
  }

  back(): void {
    this.router.navigate(['/']);
  }

  exportToCSV() {
    this.filteredTransactions$.subscribe(async transactions => {
      if (!transactions || transactions.length === 0) return;
      const header = ['Datum', 'Grund', 'Verwendet von', 'Auftragsnummer', 'Coins', 'Gewährt von'];
      const rows = transactions.map(tx => [
        tx.createdDate ? new Date(tx.createdDate).toLocaleString() : '',
        tx.reason?.reasonText || '',
        tx.customer ? `${tx.customer.userDetails?.firstName || ''} ${tx.customer.userDetails?.lastName || ''}`.trim() : '',
        tx.reflexOrderNo || '',
        tx.amount || '',
        tx.reason?.reasonText?.includes('Generiert') ? 'Niclen GmbH' : (tx.grantedBy ? `${tx.grantedBy.firstName} ${tx.grantedBy.lastName}` : '')
      ]);
      const csvContent = [header, ...rows]
        .map(e => e.map(field => `"${String(field).replace(/"/g, '""')}"`).join(','))
        .join('\r\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      if ('showSaveFilePicker' in window) {
        // @ts-ignore
        const handle = await window.showSaveFilePicker({
          suggestedName: 'meine-coin-transaktionen.csv',
          types: [{ description: 'CSV file', accept: { 'text/csv': ['.csv'] } }]
        });
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        // Fallback for browsers that don't support the API
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'meine-coin-transaktionen.csv';
        link.click();
        URL.revokeObjectURL(link.href);
      }
    }).unsubscribe();
  }
}
